import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  type RootState,
  useAppDispatch,
  useAppSelector,
} from '../../redux-store';

import { templateActions, templateSelectors } from './template.slice';
import { applyTemplate, previewTemplate } from './template.thunks';

/**
 * Hook to get all templates
 */
export const useTemplates = () =>
  useSelector((state: RootState) => templateSelectors.selectAll(state));

/**
 * Hook to get active template metadata
 */
export const useActiveTemplateMetadata = () => {
  const id = useSelector((state: RootState) =>
    templateSelectors.selectActiveTemplateId(state),
  );
  const name = useSelector((state: RootState) =>
    templateSelectors.selectActiveTemplateName(state),
  );
  const isCustom = useSelector((state: RootState) =>
    templateSelectors.selectActiveTemplateIsCustom(state),
  );

  return { id, name, isCustom };
};

/**
 * Hook to get the preview template ID
 */
export const usePreviewTemplateId = () =>
  useSelector((state: RootState) =>
    templateSelectors.selectPreviewTemplateId(state),
  );

/**
 * Hook to get the applying state (whether a template is currently being applied)
 */
export const useIsApplying = () =>
  useSelector((state: RootState) => templateSelectors.selectIsApplying(state));

/**
 * Hook to preview a template (loads it into store without marking as active)
 */
export const usePreviewTemplate = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    async (templateId: string) => {
      const result = await dispatch(previewTemplate(templateId));
      if (previewTemplate.rejected.match(result)) {
        throw new Error(result.payload || 'Failed to preview template');
      }
      return result;
    },
    [dispatch],
  );
};

/**
 * Hook to clear template preview state and restore active template
 * This discards any unsaved changes made during preview/edit
 */
export const useClearPreview = () => {
  const dispatch = useAppDispatch();
  const activeTemplateId = useAppSelector(
    state => state.template.activeTemplateId,
  );

  // Use ref to always have latest activeTemplateId without changing callback reference
  // This prevents useEffect dependency issues in consumers like MainMapPage
  const activeTemplateIdRef = useRef(activeTemplateId);
  activeTemplateIdRef.current = activeTemplateId;

  return useCallback(async () => {
    // Always restore the active template's stored layout when clearing preview
    // This ensures any edits made during preview (especially to custom templates) are discarded
    const currentActiveTemplateId = activeTemplateIdRef.current;
    if (currentActiveTemplateId) {
      // Use previewTemplate to load the active template back from stored snapshot
      // This will replace Redux state with the original template's state
      await dispatch(previewTemplate(currentActiveTemplateId));
    }

    // Clear preview state
    dispatch(templateActions.clearPreview());
  }, [dispatch]);
};

/**
 * Hook to apply a template by ID (marks it as active)
 */
export const useApplyTemplate = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    async (templateId: string) => {
      const result = await dispatch(applyTemplate(templateId));
      if (applyTemplate.rejected.match(result)) {
        throw new Error(result.payload || 'Failed to apply template');
      }
      return result;
    },
    [dispatch],
  );
};
